/**
 * plateStrength.ts - Moduł obliczania wytrzymałości płyt i pojemników
 * PlexiSystem Engineering Module
 * 
 * Poprawiona wersja z:
 * - Jednostkami SI (konwersja mm → m)
 * - Nowymi tablicami współczynników
 * - Obsługą pojemników z ciśnieniem hydrostatycznym
 * - Automatycznym żebrowaniem
 */

import { MaterialKey, getMaterialValue, MATERIALS } from '../constants/materials';
import { 
  BoundaryCondition, 
  interpolateCoefficients,
  getThicknessCorrection,
  getSafetyFactor,
  checkRibbingRequired,
  calculateRibParameters
} from '../constants/plateCoeffs';

export interface PlateInput {
  // Wymiary [mm]
  a_mm: number;                    // Krótszy bok płyty
  b_mm: number;                    // Dłuższy bok płyty
  h_mm: number;                    // Grubość płyty
  
  // Materiał
  material: MaterialKey;
  
  // Obciążenie
  load: {
    type: 'uniform' | 'hydrostatic' | 'point';
    q_kPa?: number;               // Obciążenie równomierne [kPa]
    H_mm?: number;                // Wysokość słupa cieczy [mm] 
    density_kg_m3?: number;       // Gęstość cieczy [kg/m³]
    F_N?: number;                 // Siła skupiona [N]
    conservative?: boolean;       // Czy używać pełnego ciśnienia (true) czy średniego (false)
  };
  
  // Warunki brzegowe
  support: BoundaryCondition;
  
  // Kryteria projektowe
  deflection_limit_ratio?: number; // L/X limit ugięcia (domyślnie 200)
  safety_factor?: number;          // Współczynnik bezpieczeństwa (domyślnie 2.5)
  long_term?: boolean;            // Obciążenie długotrwałe (domyślnie false)
}

export interface PlateResult {
  // Wyniki główne
  sigma_max_MPa: number;           // Maksymalne naprężenia [MPa]
  w_max_mm: number;                // Maksymalne ugięcie [mm]
  
  // Wartości dopuszczalne
  sigma_allow_MPa: number;         // Naprężenia dopuszczalne [MPa]
  deflection_limit_mm: number;     // Limit ugięcia [mm]
  
  // Współczynniki wykorzystania
  utilization_stress: number;      // σ_max / σ_allow (0-1)
  utilization_deflection: number;  // w_max / w_limit (0-1)
  safety_factor_actual: number;    // Rzeczywisty współczynnik bezpieczeństwa
  
  // Status
  pass: boolean;                   // Czy spełnia wszystkie kryteria
  executable: boolean;             // Czy wykonalne technicznie
  
  // Zalecenia
  recommendations: string[];       
  warnings: string[];              
  h_recommended?: number;          // Zalecana grubość [mm]
  
  // Żebrowanie
  ribs?: {                        
    required: boolean;
    count: number;
    spacing_mm: number;
    height_mm: number;
    thickness_mm: number;
  };
  
  // Dane obliczeniowe
  calculation_details: {
    a_m: number;
    b_m: number;
    t_m: number;
    q_Pa: number;
    E_Pa: number;
    aspect_ratio: number;
    Csig: number;
    Cw: number;
  };
}

/**
 * Główna funkcja obliczania wytrzymałości płyt
 */
export function calcPlateStrength(input: PlateInput): PlateResult {
  // 1. PRZYGOTOWANIE DANYCH
  
  // Upewnij się że a < b (a to krótszy bok)
  let a_mm = Math.min(input.a_mm, input.b_mm);
  let b_mm = Math.max(input.a_mm, input.b_mm);
  const t_mm = input.h_mm;
  
  // Konwersja jednostek na SI (mm → m)
  const a_m = a_mm / 1000;
  const b_m = b_mm / 1000;
  const t_m = t_mm / 1000;
  
  // Pobierz właściwości materiału
  const E_MPa = getMaterialValue(input.material, 'E_MPa');
  const E_Pa = E_MPa * 1e6; // MPa → Pa
  
  const nu = getMaterialValue(input.material, 'nu');
  const sigma_yield_MPa = getMaterialValue(input.material, 'sigma_allow_MPa', false, true); // Użyj max jako granicy plastyczności
  
  // 2. OBLICZ OBCIĄŻENIE [Pa]
  
  let q_Pa: number = 0;
  let loadDescription = '';
  
  if (input.load.type === 'uniform' && input.load.q_kPa !== undefined) {
    // Obciążenie równomierne
    q_Pa = input.load.q_kPa * 1000; // kPa → Pa
    loadDescription = `Równomierne: ${input.load.q_kPa} kPa`;
    
  } else if (input.load.type === 'hydrostatic' && input.load.H_mm) {
    // Ciśnienie hydrostatyczne (pojemnik z cieczą)
    const H_m = input.load.H_mm / 1000;
    const rho = input.load.density_kg_m3 || 1000; // domyślnie woda
    const g = 9.81;
    
    const p_max = rho * g * H_m; // Ciśnienie na dnie
    
    if (input.load.conservative !== false) {
      // Konserwatywne: używamy pełnego ciśnienia
      q_Pa = p_max;
      loadDescription = `Hydrostatyczne (konserwatywne): ${(p_max/1000).toFixed(1)} kPa`;
    } else {
      // Fizyczne: używamy ciśnienia średniego (trójkątny rozkład)
      q_Pa = 0.5 * p_max;
      loadDescription = `Hydrostatyczne (średnie): ${(q_Pa/1000).toFixed(1)} kPa`;
    }
    
  } else if (input.load.type === 'point' && input.load.F_N) {
    // Dla siły skupionej - przelicz na obciążenie zastępcze
    q_Pa = input.load.F_N / (a_m * b_m);
    loadDescription = `Punktowe: ${input.load.F_N} N`;
  } else {
    throw new Error('Nieprawidłowe dane obciążenia');
  }
  
  // 3. OBLICZ WSPÓŁCZYNNIKI
  
  const aspect_ratio = a_m / b_m; // Zawsze ≤ 1 bo a ≤ b
  const aspect_ratio_inv = b_m / a_m; // Do tablic (≥ 1)
  
  const { Csig, Cw } = interpolateCoefficients(aspect_ratio_inv, input.support);
  
  // 4. OBLICZ NAPRĘŻENIA I UGIĘCIA
  
  // Naprężenia maksymalne: σ_max = Cσ * (q * a² / t²)
  const sigma_Pa = Csig * (q_Pa * a_m * a_m) / (t_m * t_m);
  const sigma_MPa = sigma_Pa / 1e6;
  
  // Ugięcie maksymalne: w_max = Cw * (q * a⁴) / (E * t³)
  const w_m = Cw * (q_Pa * Math.pow(a_m, 4)) / (E_Pa * Math.pow(t_m, 3));
  const w_mm = w_m * 1000;
  
  // 5. OKREŚL KRYTERIA DOPUSZCZALNOŚCI
  
  // Współczynnik bezpieczeństwa
  const materialType = input.material === 'PMMA' || input.material === 'PC' ? 'brittle' : 'ductile';
  const loadType = input.load.type === 'hydrostatic' ? 'hydrostatic' : 'static';
  const SF_required = input.safety_factor || getSafetyFactor(loadType, materialType, input.long_term);
  
  // Naprężenia dopuszczalne
  let sigma_allow_MPa = sigma_yield_MPa / SF_required;
  
  // Redukcja dla obciążeń długotrwałych (pełzanie)
  if (input.long_term) {
    sigma_allow_MPa *= 0.6; // Redukcja o 40% dla tworzyw
  }
  
  // Limit ugięcia
  const deflection_limit_ratio = input.deflection_limit_ratio || 200;
  const deflection_limit_mm = a_mm / deflection_limit_ratio;
  
  // 6. SPRAWDŹ KRYTERIA
  
  const utilization_stress = sigma_MPa / sigma_allow_MPa;
  const utilization_deflection = w_mm / deflection_limit_mm;
  const safety_factor_actual = sigma_yield_MPa / sigma_MPa;
  
  const stress_ok = utilization_stress <= 1.0;
  const deflection_ok = utilization_deflection <= 1.0;
  const pass = stress_ok && deflection_ok;
  
  // 7. SPRAWDŹ POTRZEBĘ ŻEBROWANIA
  
  const ribbingCheck = checkRibbingRequired(a_mm, t_mm, input.load.H_mm);
  let ribs = undefined;
  
  if (ribbingCheck.required || !pass) {
    const ribParams = calculateRibParameters(t_mm, a_mm, q_Pa);
    ribs = {
      required: true,
      ...ribParams
    };
  }
  
  // 8. GENERUJ ZALECENIA
  
  const recommendations: string[] = [];
  const warnings: string[] = [];
  let h_recommended: number | undefined;
  let executable = true;
  
  // Jeśli nie spełnia kryteriów
  if (!pass) {
    // Oblicz zalecaną grubość dla naprężeń
    if (!stress_ok) {
      // t_new = t_old * sqrt(σ_max / σ_allow)
      const h_stress = t_mm * Math.sqrt(utilization_stress);
      h_recommended = Math.ceil(h_stress);
      recommendations.push(`Zwiększ grubość do ${h_recommended}mm dla spełnienia kryterium naprężeń`);
    }
    
    // Oblicz zalecaną grubość dla ugięcia
    if (!deflection_ok) {
      // t_new = t_old * (w_max / w_limit)^(1/3)
      const h_deflection = t_mm * Math.pow(utilization_deflection, 1/3);
      h_recommended = Math.max(h_recommended || 0, Math.ceil(h_deflection));
      recommendations.push(`Zwiększ grubość do ${Math.ceil(h_deflection)}mm dla spełnienia kryterium ugięcia`);
    }
    
    // Jeśli grubość > 15mm, zaproponuj żebra
    if (h_recommended && h_recommended > 15) {
      recommendations.push(`Alternatywa: Zastosuj żebrowanie zamiast zwiększania grubości`);
    }
    
    // Sprawdź wykonywalność
    if (h_recommended && h_recommended > 30) {
      executable = false;
      warnings.push('NIEWYKONALNE: Wymagana grubość > 30mm. Konieczna zmiana konstrukcji.');
    } else if (w_mm > 50) {
      executable = false;
      warnings.push(`NIEWYKONALNE: Ugięcie ${w_mm.toFixed(1)}mm > 50mm. Konstrukcja niestabilna.`);
    }
  }
  
  // Dodatkowe ostrzeżenia
  if (ribbingCheck.required && ribbingCheck.recommendation) {
    warnings.push(ribbingCheck.recommendation);
  }
  
  if (aspect_ratio_inv > 4) {
    warnings.push('Stosunek boków > 4:1 - rozważ podział na mniejsze sekcje');
  }
  
  if (t_mm < 2) {
    warnings.push('Grubość < 2mm może być trudna w obróbce i podatna na uszkodzenia');
  }
  
  if (t_mm > 20 && !ribs) {
    warnings.push('Grubość > 20mm - rozważ konstrukcję żebrowaną dla oszczędności materiału');
  }
  
  // Specjalne ostrzeżenie dla pojemników
  if (input.load.type === 'hydrostatic' && input.load.H_mm) {
    if (input.load.H_mm >= 300 && t_mm < 4 && !ribs) {
      warnings.push(`UWAGA: Wysokość słupa wody ${input.load.H_mm}mm przy grubości ${t_mm}mm. Ryzyko wybrzuszeń lokalnych - WYMAGANE żebra co 150-200mm!`);
      executable = false;
    }
  }
  
  // Test kanoniczny
  if (a_mm === 300 && b_mm === 200 && input.load.H_mm === 1000 && t_mm === 3 && input.material === 'PMMA') {
    console.log('TEST KANONICZNY - Pojemnik 300×200×1000mm, plexi 3mm:');
    console.log(`- σ_max = ${sigma_MPa.toFixed(1)} MPa (oczekiwane: 4-5 MPa)`);
    console.log(`- w_max = ${w_mm.toFixed(1)} mm (oczekiwane: 20-40 mm)`);
    console.log(`- Status: ${executable ? 'Wykonalny' : 'NIEWYKONALNY'}`);
  }
  
  // 9. ZWRÓĆ WYNIKI
  
  return {
    // Wyniki główne
    sigma_max_MPa: Math.round(sigma_MPa * 10) / 10,
    w_max_mm: Math.round(w_mm * 100) / 100,
    
    // Dopuszczalne
    sigma_allow_MPa: Math.round(sigma_allow_MPa * 10) / 10,
    deflection_limit_mm: Math.round(deflection_limit_mm * 100) / 100,
    
    // Wykorzystanie
    utilization_stress: Math.round(utilization_stress * 100) / 100,
    utilization_deflection: Math.round(utilization_deflection * 100) / 100,
    safety_factor_actual: Math.round(safety_factor_actual * 10) / 10,
    
    // Status
    pass,
    executable,
    
    // Zalecenia
    recommendations,
    warnings,
    h_recommended,
    ribs,
    
    // Szczegóły obliczeniowe
    calculation_details: {
      a_m,
      b_m,
      t_m,
      q_Pa,
      E_Pa,
      aspect_ratio,
      Csig,
      Cw
    }
  };
}

/**
 * Oblicza naprężenia w cylindrycznej tubie (ciśnienie wewnętrzne)
 */
export function calcCylindricalTube(input: {
  D_mm: number;           // Średnica wewnętrzna [mm]
  t_mm: number;           // Grubość ścianki [mm]
  H_mm: number;           // Wysokość tuby [mm]
  p_kPa: number;          // Ciśnienie wewnętrzne [kPa]
  material: MaterialKey;
  safety_factor?: number;
}): {
  sigma_hoop_MPa: number;      // Naprężenia obwodowe
  sigma_long_MPa: number;      // Naprężenia wzdłużne
  sigma_allow_MPa: number;
  pass: boolean;
  recommendations: string[];
} {
  // Konwersja jednostek
  const D_m = input.D_mm / 1000;
  const t_m = input.t_mm / 1000;
  const p_Pa = input.p_kPa * 1000;
  
  // Właściwości materiału
  const sigma_yield_MPa = getMaterialValue(input.material, 'sigma_allow_MPa', false, true);
  const SF = input.safety_factor || 2.5;
  const sigma_allow_MPa = sigma_yield_MPa / SF;
  
  // Naprężenia obwodowe (hoop stress): σ_h = p*D / (2*t)
  const sigma_hoop_Pa = (p_Pa * D_m) / (2 * t_m);
  const sigma_hoop_MPa = sigma_hoop_Pa / 1e6;
  
  // Naprężenia wzdłużne: σ_l = p*D / (4*t) (dla zamkniętych końców)
  const sigma_long_Pa = (p_Pa * D_m) / (4 * t_m);
  const sigma_long_MPa = sigma_long_Pa / 1e6;
  
  // Sprawdzenie
  const sigma_max_MPa = Math.max(sigma_hoop_MPa, sigma_long_MPa);
  const pass = sigma_max_MPa <= sigma_allow_MPa;
  
  const recommendations: string[] = [];
  if (!pass) {
    const t_required = (p_Pa * D_m) / (2 * sigma_allow_MPa * 1e6);
    const t_required_mm = Math.ceil(t_required * 1000);
    recommendations.push(`Zwiększ grubość ścianki do ${t_required_mm}mm`);
  }
  
  if (input.D_mm / input.t_mm > 50) {
    recommendations.push('Stosunek D/t > 50 - ryzyko utraty stateczności');
  }
  
  return {
    sigma_hoop_MPa: Math.round(sigma_hoop_MPa * 10) / 10,
    sigma_long_MPa: Math.round(sigma_long_MPa * 10) / 10,
    sigma_allow_MPa: Math.round(sigma_allow_MPa * 10) / 10,
    pass,
    recommendations
  };
}

/**
 * Sprawdza możliwość gięcia na zimno
 */
export function checkColdBending(
  t_mm: number,
  r_mm: number,
  material: MaterialKey
): {
  feasible: boolean;
  min_radius_mm: number;
  recommendation?: string;
} {
  // Minimalny promień gięcia: r_min = 3*t (dla większości tworzyw)
  const min_radius_mm = 3 * t_mm;
  const feasible = r_mm >= min_radius_mm;
  
  return {
    feasible,
    min_radius_mm,
    recommendation: feasible 
      ? undefined 
      : `Promień gięcia ${r_mm}mm < minimum ${min_radius_mm}mm. Ryzyko pęknięć.`
  };
}

/**
 * Oblicza parametry połączenia klejonego
 */
export function calcAdhesiveJoint(input: {
  F_N: number;              // Siła w połączeniu [N]
  L_mm: number;             // Długość spoiny [mm]
  w_mm: number;             // Szerokość zakładki [mm]
  adhesive_type: 'uv' | 'solvent' | 'epoxy';
}): {
  tau_MPa: number;          // Naprężenia ścinające
  tau_allow_MPa: number;    // Dopuszczalne
  pass: boolean;
  A_mm2: number;            // Powierzchnia spoiny
} {
  // Powierzchnia spoiny
  const A_mm2 = input.L_mm * input.w_mm;
  const A_m2 = A_mm2 / 1e6;
  
  // Naprężenia ścinające
  const tau_Pa = input.F_N / A_m2;
  const tau_MPa = tau_Pa / 1e6;
  
  // Dopuszczalne naprężenia dla różnych klejów
  const tau_allow_map = {
    'uv': 5.0,        // Klej UV
    'solvent': 4.0,   // Klej rozpuszczalnikowy
    'epoxy': 8.0      // Klej epoksydowy
  };
  
  const tau_allow_MPa = tau_allow_map[input.adhesive_type];
  const pass = tau_MPa <= tau_allow_MPa;
  
  return {
    tau_MPa: Math.round(tau_MPa * 10) / 10,
    tau_allow_MPa,
    pass,
    A_mm2
  };
}
